// Payments/WithdrawalHistoryTable.js

import React, { useState, useEffect } from 'react';
import axiosInstance from "../../utils/axiosInstance";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, CircularProgress, Alert, Box, TextField, IconButton, Tooltip,
  Stack, Chip, Typography, useTheme, alpha
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import PendingIcon from '@mui/icons-material/Pending';

const WithdrawalHistoryTable = ({ perPage = 10, showPagination = true, refreshKey }) => {
  const theme = useTheme();
  const [withdrawals, setWithdrawals] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(perPage);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [checkingId, setCheckingId] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    const params = {
      page: page + 1,
      per_page: rowsPerPage,
      start_date: startDate || null,
      end_date: endDate || null,
    };
    axiosInstance.get(`/payments/withdrawals`, { params })
      .then(res => {
        setWithdrawals(res.data.data);
        setTotalRows(res.data.total);
      })
      .catch(err => setError('Failed to fetch withdrawal history.'))
      .finally(() => setIsLoading(false));
  }, [page, rowsPerPage, startDate, endDate, refreshKey]);

  const handleCheckStatus = async (transactionId) => {
    setCheckingId(transactionId);
    try {
      const res = await axiosInstance.post(`/payments/withdrawals/${transactionId}/check-status`, {});
      const newStatus = res.data.new_status;

      setWithdrawals(currentWithdrawals => 
        currentWithdrawals.map(w => 
          w.id === transactionId ? { ...w, status: newStatus } : w
        )
      );
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to check status.');
    } finally {
      setCheckingId(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      {showPagination && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
        </Stack>
      )}
      <Paper elevation={2}>
        <TableContainer sx={{
          // FIX: Added custom scrollbar styling that adapts to the theme
          scrollbarWidth: 'thin',
          scrollbarColor: `${theme.palette.divider} ${theme.palette.background.paper}`,
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.background.paper,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.divider,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          },
        }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>S.no</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>Datetime</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>Gateway</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>Destination Account ID</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>Gateway Payout ID</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawals.map((row) => (
                <TableRow 
                  hover 
                  key={row.id}
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                >
                  <TableCell>{row.s_no}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.datetime}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="primary">
                      ${row.amount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.status} 
                      size="small" 
                      color={getStatusColor(row.status)}
                      icon={getStatusIcon(row.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={row.gateway} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {row.destination_account_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {row.gateway_payout_id}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {row.status === 'pending' && (
                      <Tooltip title="Check Status">
                        <IconButton
                          onClick={() => handleCheckStatus(row.id)}
                          disabled={checkingId === row.id}
                          size="small"
                          color="primary"
                        >
                          {checkingId === row.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SyncIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    {row.status === 'succeeded' && getStatusIcon(row.status)}
                    {row.status === 'failed' && getStatusIcon(row.status)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {showPagination && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Box>
  );
};

export default WithdrawalHistoryTable;